import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Headphones, Loader2, Square, Play, AlertCircle } from 'lucide-react'
import { supabase } from '../../../lib/supabaseClient'
import { useTextToSpeech } from '../../../hooks/useTextToSpeech'
import { cn } from '../../../lib/utils'

// Morning voice briefing — round button in the Today greeting strip.
//
// Two-tap by design: generating the briefing takes seconds, and iOS
// only allows audio playback inside a user-gesture window — playing
// after the network round-trip gets silently blocked. So tap 1
// generates (spinner → the button flips to ▶), tap 2 plays inside a
// fresh gesture. The text is kept for the session so replays are
// instant, and failures turn the button red instead of doing nothing.

export default function DailyBriefingButton() {
  const { t } = useTranslation()
  const tts = useTextToSpeech()
  const [busy, setBusy] = useState(false)
  const [briefing, setBriefing] = useState(null)
  const [failed, setFailed] = useState(false)

  const tap = async () => {
    if (tts.playing) { tts.stop(); return }
    if (busy) return

    // Tap 2 (or replay): text is ready — play inside this gesture.
    if (briefing) {
      tts.play(briefing, 'ar')
      return
    }

    // Tap 1: generate.
    setBusy(true)
    setFailed(false)
    const { data, error } = await supabase.functions.invoke('daily-briefing', { body: {} })
    setBusy(false)
    if (error || !data?.briefing) {
      setFailed(true)
      setTimeout(() => setFailed(false), 4000)
      return
    }
    setBriefing(data.briefing)
    // Try to start right away — works where the gesture window is
    // lenient (desktop/Android); on iOS the ▶ state catches the rest.
    tts.play(data.briefing, 'ar')
  }

  const label = failed
    ? t('briefing.failed')
    : tts.playing
      ? t('briefing.stop')
      : briefing
        ? t('briefing.ready')
        : t('briefing.play')

  return (
    <button
      type="button"
      onClick={tap}
      aria-label={label}
      title={label}
      className={cn(
        'w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0 transition-colors',
        failed
          ? 'bg-red-500/15 text-red-500'
          : tts.playing
            ? 'bg-app-primary-500 text-white'
            : briefing
              ? 'bg-emerald-500/15 text-emerald-500'
              : 'bg-app-primary-500/10 text-app-primary-500 hover:bg-app-primary-500/20',
      )}
    >
      {busy
        ? <Loader2 className="w-4 h-4 animate-spin" />
        : failed
          ? <AlertCircle className="w-4 h-4" />
          : tts.playing
            ? <Square className="w-4 h-4" />
            : briefing
              ? <Play className="w-4 h-4 rtl:-scale-x-100" />
              : <Headphones className="w-4 h-4" />}
    </button>
  )
}
